import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { searchApi } from '../api/search';
import Map from '../components/Map';
import type { Listing } from '../types/index.js';
import { useAuth } from '../context/AuthContext';

/** Technische Statuswerte aus dem Backend für die Anzeige eindeutschen. */
const SEARCH_STATUS_LABELS: Record<string, string> = {
  pending: 'wartet',
  in_progress: 'läuft',
  completed: 'abgeschlossen',
  failed: 'fehlgeschlagen',
};

const DETAIL_STATUS_LABELS: Record<string, string> = {
  pending: 'wartet',
  in_progress: 'läuft',
  completed: 'abgeschlossen',
  failed: 'fehlgeschlagen',
};

const parseNumericValue = (value: string | null): number | null => {
  if (!value) return null;
  const match = value.replace(/,/g, '.').match(/[\d.]+/);
  return match ? parseFloat(match[0]) : null;
};

const parseDistanceKm = (value: string | null): number | null => {
  if (!value) return null;
  const match = value.replace(/,/g, '.').match(/(\d+(?:\.\d+)?)\s*(km|m)\b/i);
  if (!match) return parseNumericValue(value);
  const num = parseFloat(match[1]);
  return match[2].toLowerCase() === 'm' ? num / 1000 : num;
};

const SearchResults: React.FC = () => {
  const { searchId } = useParams<{ searchId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [downloading, setDownloading] = useState(false);
  const [scrapingDetails, setScrapingDetails] = useState(false);
  const [hoveredListingId, setHoveredListingId] = useState<number | null>(null);
  const [hoverSource, setHoverSource] = useState<'map' | 'list' | null>(null);
  const listingRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const listContainerRef = useRef<HTMLDivElement>(null);
  const [showFilters, setShowFilters] = useState(searchParams.get('showFilters') === 'true');
  const [filters, setFilters] = useState({
    priceMin: searchParams.get('priceMin') || '',
    priceMax: searchParams.get('priceMax') || '',
    distanceMax: searchParams.get('distanceMax') || '',
    sizeMin: searchParams.get('sizeMin') || '',
    sizeMax: searchParams.get('sizeMax') || '',
    guestsMin: searchParams.get('guestsMin') || '',
    bedroomsMin: searchParams.get('bedroomsMin') || '',
    reviewsMin: searchParams.get('reviewsMin') || '',
    hideSponsored: searchParams.get('hideSponsored') === 'true',
    detailHasDates: searchParams.get('detailHasDates') === 'true',
    detailNoDates: searchParams.get('detailNoDates') === 'true',
    detailFailed: searchParams.get('detailFailed') === 'true',
    detailNotScraped: searchParams.get('detailNotScraped') === 'true',
  });
  const [sortField, setSortField] = useState(searchParams.get('sortField') || '');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>((searchParams.get('sortDirection') as 'asc' | 'desc') || 'asc');
  const { user } = useAuth();
  const cityCenter: [number, number] | undefined = user?.city?.default_map_center
    ? [user.city.default_map_center.latitude, user.city.default_map_center.longitude]
    : undefined;
  const [mapCenter, setMapCenter] = useState<[number, number] | undefined>(() => {
    const lat = searchParams.get('mapLat');
    const lng = searchParams.get('mapLng');
    if (lat && lng) return [parseFloat(lat), parseFloat(lng)];
    return cityCenter;
  });
  const [mapZoom, setMapZoom] = useState<number>(() => {
    const zoom = searchParams.get('mapZoom');
    return zoom ? parseInt(zoom) : 11;
  });
  const [scrollPosition, setScrollPosition] = useState<number>(() => {
    const pos = searchParams.get('scrollPos');
    return pos ? parseInt(pos) : 0;
  });

  const { data: search, refetch: refetchSearch } = useQuery({
    queryKey: ['search', searchId],
    queryFn: () => searchApi.getSearch(Number(searchId)),
    enabled: !!searchId,
  });

  const { data: listings } = useQuery({
    queryKey: ['listings', searchId],
    queryFn: () => searchApi.getListingsBySearch(Number(searchId)),
    enabled: !!searchId && search?.search_status === 'completed',
  });

  // Poll for search completion
  useEffect(() => {
    if (search?.search_status === 'in_progress' || search?.search_status === 'pending') {
      const interval = setInterval(() => {
        refetchSearch();
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [search?.search_status, refetchSearch]);

  // Poll for detail scrape progress
  const { data: detailProgress, refetch: refetchDetailProgress } = useQuery({
    queryKey: ['detail-progress', searchId],
    queryFn: () => searchApi.getDetailProgress(Number(searchId)),
    enabled: !!searchId && search?.detail_scrape_status === 'in_progress',
    refetchInterval: search?.detail_scrape_status === 'in_progress' ? 2000 : false,
  });

  // Refetch search + listings when detail scrape completes
  useEffect(() => {
    if (detailProgress?.detail_scrape_status === 'completed' || detailProgress?.detail_scrape_status === 'failed') {
      refetchSearch();
    }
  }, [detailProgress?.detail_scrape_status, refetchSearch]);

  // Scroll to listing card when a map pin is hovered
  useEffect(() => {
    if (hoveredListingId && hoverSource === 'map') {
      const el = listingRefs.current[hoveredListingId];
      if (el && listContainerRef.current) {
        const container = listContainerRef.current;
        const elTop = el.offsetTop - container.offsetTop;
        const elBottom = elTop + el.offsetHeight;
        const scrollTop = container.scrollTop;
        const containerHeight = container.clientHeight;

        // Only scroll if the element is not already visible
        if (elTop < scrollTop || elBottom > scrollTop + containerHeight) {
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }
  }, [hoveredListingId, hoverSource]);

  const handleMapHover = useCallback((id: number | null) => {
    setHoveredListingId(id);
    setHoverSource(id ? 'map' : null);
  }, []);

  const handleListHover = useCallback((id: number | null) => {
    setHoveredListingId(id);
    setHoverSource(id ? 'list' : null);
  }, []);

  const handleMapMove = useCallback((center: [number, number], zoom: number) => {
    setMapCenter(center);
    setMapZoom(zoom);
  }, []);

  const filteredListings = useMemo(() => {
    if (!listings) return [];
    let result = listings.filter((listing) => {
      if (filters.priceMin && (listing.price_per_night == null || listing.price_per_night < Number(filters.priceMin))) return false;
      if (filters.priceMax && (listing.price_per_night == null || listing.price_per_night > Number(filters.priceMax))) return false;
      if (filters.distanceMax) {
        const dist = parseDistanceKm(listing.distance_to_center);
        if (dist == null || dist > Number(filters.distanceMax)) return false;
      }
      if (filters.sizeMin) {
        const size = parseNumericValue(listing.size);
        if (size == null || size < Number(filters.sizeMin)) return false;
      }
      if (filters.sizeMax) {
        const size = parseNumericValue(listing.size);
        if (size == null || size > Number(filters.sizeMax)) return false;
      }
      if (filters.guestsMin && (listing.guests_count == null || listing.guests_count < Number(filters.guestsMin))) return false;
      if (filters.bedroomsMin && (listing.bedrooms == null || listing.bedrooms < Number(filters.bedroomsMin))) return false;
      if (filters.reviewsMin) {
        const reviews = parseNumericValue(listing.reviews);
        if (reviews == null || reviews < Number(filters.reviewsMin)) return false;
      }
      if (filters.hideSponsored && listing.sponsored && listing.sponsored !== 'None') return false;
      const anyDetailFilter = filters.detailHasDates || filters.detailNoDates || filters.detailFailed || filters.detailNotScraped;
      if (anyDetailFilter) {
        const hasDates = listing.detail_scrape_status === 'completed' && !!listing.booked_dates;
        const noDates = listing.detail_scrape_status === 'completed' && !listing.booked_dates;
        const failed = listing.detail_scrape_status === 'failed';
        const notScraped = !listing.detail_scrape_status || listing.detail_scrape_status === 'pending' || listing.detail_scrape_status === 'in_progress';
        if (!(
          (filters.detailHasDates && hasDates) ||
          (filters.detailNoDates && noDates) ||
          (filters.detailFailed && failed) ||
          (filters.detailNotScraped && notScraped)
        )) return false;
      }
      return true;
    });
    if (sortField) {
      result = [...result].sort((a, b) => {
        let aVal: number | null = null;
        let bVal: number | null = null;
        switch (sortField) {
          case 'price': aVal = a.price_per_night; bVal = b.price_per_night; break;
          case 'distance': aVal = parseDistanceKm(a.distance_to_center); bVal = parseDistanceKm(b.distance_to_center); break;
          case 'size': aVal = parseNumericValue(a.size); bVal = parseNumericValue(b.size); break;
          case 'guests': aVal = a.guests_count; bVal = b.guests_count; break;
          case 'bedrooms': aVal = a.bedrooms; bVal = b.bedrooms; break;
          case 'reviews': aVal = parseNumericValue(a.reviews); bVal = parseNumericValue(b.reviews); break;
          case 'sponsored':
            aVal = (a.sponsored && a.sponsored !== 'None') ? 1 : 0;
            bVal = (b.sponsored && b.sponsored !== 'None') ? 1 : 0;
            break;
        }
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      });
    }
    return result;
  }, [listings, filters, sortField, sortDirection]);

  const filtersActive = !!(filters.priceMin || filters.priceMax || filters.distanceMax || filters.sizeMin || filters.sizeMax || filters.guestsMin || filters.bedroomsMin || filters.reviewsMin || filters.hideSponsored || filters.detailHasDates || filters.detailNoDates || filters.detailFailed || filters.detailNotScraped);

  const resetFilters = () => {
    setFilters({ priceMin: '', priceMax: '', distanceMax: '', sizeMin: '', sizeMax: '', guestsMin: '', bedroomsMin: '', reviewsMin: '', hideSponsored: false, detailHasDates: false, detailNoDates: false, detailFailed: false, detailNotScraped: false });
    setSortField('');
    setSortDirection('asc');
  };

  const updateFilter = (key: string, value: string | boolean) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Track scroll position with debounce
  useEffect(() => {
    const container = listContainerRef.current;
    if (!container) return;
    let timeoutId: number;
    const handleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        setScrollPosition(container.scrollTop);
      }, 300);
    };
    container.addEventListener('scroll', handleScroll);
    return () => {
      clearTimeout(timeoutId);
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Restore scroll position on mount (only once)
  const scrollRestored = useRef(false);
  useEffect(() => {
    const container = listContainerRef.current;
    if (container && scrollPosition > 0 && !scrollRestored.current && filteredListings.length > 0) {
      container.scrollTop = scrollPosition;
      scrollRestored.current = true;
    }
  }, [scrollPosition, filteredListings]);

  // Sync state to URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (showFilters) params.set('showFilters', 'true');
    if (filters.priceMin) params.set('priceMin', filters.priceMin);
    if (filters.priceMax) params.set('priceMax', filters.priceMax);
    if (filters.distanceMax) params.set('distanceMax', filters.distanceMax);
    if (filters.sizeMin) params.set('sizeMin', filters.sizeMin);
    if (filters.sizeMax) params.set('sizeMax', filters.sizeMax);
    if (filters.guestsMin) params.set('guestsMin', filters.guestsMin);
    if (filters.bedroomsMin) params.set('bedroomsMin', filters.bedroomsMin);
    if (filters.reviewsMin) params.set('reviewsMin', filters.reviewsMin);
    if (filters.hideSponsored) params.set('hideSponsored', 'true');
    if (filters.detailHasDates) params.set('detailHasDates', 'true');
    if (filters.detailNoDates) params.set('detailNoDates', 'true');
    if (filters.detailFailed) params.set('detailFailed', 'true');
    if (filters.detailNotScraped) params.set('detailNotScraped', 'true');
    if (sortField) params.set('sortField', sortField);
    if (sortDirection !== 'asc') params.set('sortDirection', sortDirection);
    if (mapCenter) {
      params.set('mapLat', mapCenter[0].toString());
      params.set('mapLng', mapCenter[1].toString());
    }
    if (mapZoom !== 11) params.set('mapZoom', mapZoom.toString());
    if (scrollPosition > 0) params.set('scrollPos', scrollPosition.toString());
    setSearchParams(params, { replace: true });
  }, [filters, sortField, sortDirection, showFilters, mapCenter, mapZoom, scrollPosition, setSearchParams]);

  const handleExport = async () => {
    if (!searchId) return;

    setDownloading(true);
    try {
      const blob = await searchApi.exportListings(Number(searchId));
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inserate_${search?.location}_${searchId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export der Inserate fehlgeschlagen.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDetailScrape = async (mode: 'all' | 'marked') => {
    if (!searchId) return;
    setScrapingDetails(true);
    try {
      await searchApi.startDetailScrape(Number(searchId), mode);
      refetchSearch();
      refetchDetailProgress();
    } catch (error: any) {
      console.error('Detail scrape failed:', error);
      alert(error?.response?.data?.detail || 'Detail-Aktualisierung konnte nicht gestartet werden.');
    } finally {
      setScrapingDetails(false);
    }
  };

  const handleUnmarkAll = async () => {
    if (!searchId) return;
    try {
      await searchApi.unmarkAllListings(Number(searchId));
      queryClient.invalidateQueries({ queryKey: ['listings', searchId] });
    } catch (error) {
      console.error('Unmark all failed:', error);
    }
  };

  const handleToggleMark = async (e: React.MouseEvent, listingId: number) => {
    e.stopPropagation();
    try {
      await searchApi.toggleListingMark(listingId);
      queryClient.invalidateQueries({ queryKey: ['listings', searchId] });
    } catch (error) {
      console.error('Toggle mark failed:', error);
    }
  };

  if (!search) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Suche wird geladen…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-blue-600 hover:text-blue-800 text-sm mb-2"
              >
                ← Zurück zum Dashboard
              </button>
              <h1 className="text-2xl font-bold text-gray-900">
                Suchergebnisse: {search.location}
              </h1>
              <p className="text-sm text-gray-600">
                Status: <span className="font-medium">{SEARCH_STATUS_LABELS[search.search_status] ?? search.search_status}</span>
                {search.search_status === 'completed' && (
                  <span> • {search.total_listings_found} Inserate gefunden</span>
                )}
                {search.search_status === 'completed' && search.max_listings > 0 && (
                  <span className="text-orange-600"> • Testmodus (max. {search.max_listings})</span>
                )}
              </p>
              {search.search_status === 'completed' && (search.total_dom_cards > 0 || search.filter_summary) && (
                <p className="text-xs text-gray-500 mt-1">
                  {search.total_dom_cards > 0 && (
                    <span>
                      {search.total_dom_cards} Treffer auf der Ergebnisseite
                      {search.total_dom_cards - search.total_before_filter > 0 && (
                        <span> → {search.total_dom_cards - search.total_before_filter} Duplikate entfernt</span>
                      )}
                    </span>
                  )}
                  {search.filter_summary && (
                    <span>
                      {search.total_dom_cards > 0 && ' → '}
                      {search.total_before_filter - search.total_listings_found} ausgefiltert:
                      {Object.entries(JSON.parse(search.filter_summary) as Record<string, number>)
                        .sort(([, a], [, b]) => b - a)
                        .map(([word, count]) => ` ${word} (${count})`)
                        .join(',')}
                    </span>
                  )}
                </p>
              )}
            </div>
            {search.search_status === 'completed' && listings && listings.length > 0 && (
              <div className="flex items-center gap-3">
                {(!search.detail_scrape_status || search.detail_scrape_status === 'failed' || search.detail_scrape_status === 'completed') && (
                  <>
                    {listings.some(l => l.marked_for_detail) && (
                      <>
                        <button
                          onClick={() => handleDetailScrape('marked')}
                          disabled={scrapingDetails}
                          className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 disabled:opacity-50"
                        >
                          {scrapingDetails ? 'Starte…' : `Markierte aktualisieren (${listings.filter(l => l.marked_for_detail).length})`}
                        </button>
                        <button
                          onClick={handleUnmarkAll}
                          className="border border-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-50"
                        >
                          Markierungen aufheben
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleDetailScrape('all')}
                      disabled={scrapingDetails}
                      className="bg-purple-400 text-white px-4 py-2 rounded hover:bg-purple-500 disabled:opacity-50"
                    >
                      {scrapingDetails ? 'Starte…' : 'Alle aktualisieren'}
                    </button>
                  </>
                )}
                <button
                  onClick={handleExport}
                  disabled={downloading}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {downloading ? 'Export läuft…' : 'Nach Excel exportieren'}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Detail Scrape Progress */}
      {(search.detail_scrape_status === 'in_progress' || search.detail_scrape_status === 'pending') && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-purple-800">
                Aktualisiere Inserat-Details…
              </span>
              <span className="text-sm text-purple-600">
                {detailProgress?.detail_scrape_progress || 0} / {detailProgress?.detail_scrape_total || 0}
              </span>
            </div>
            <div className="w-full bg-purple-200 rounded-full h-2.5">
              <div
                className="bg-purple-600 h-2.5 rounded-full transition-all duration-500"
                style={{
                  width: `${((detailProgress?.detail_scrape_progress || 0) / (detailProgress?.detail_scrape_total || 1)) * 100}%`
                }}
              ></div>
            </div>
          </div>
        </div>
      )}
      {search.detail_scrape_status === 'completed' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
            <span className="text-green-600 text-sm">Detail-Aktualisierung für {search.detail_scrape_total || search.total_listings_found} Inserate abgeschlossen</span>
          </div>
        </div>
      )}

      {/* Filter & Sort Bar */}
      {search.search_status === 'completed' && listings && listings.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-white rounded-lg shadow">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="w-full px-4 py-3 flex justify-between items-center text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span>Filter & Sortierung</span>
                {filtersActive && (
                  <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                    {filteredListings.length} / {listings.length}
                  </span>
                )}
              </div>
              <svg className={`w-5 h-5 transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showFilters && (
              <div className="px-4 pb-4 border-t border-gray-200">
                {/* Sort row */}
                <div className="flex items-center gap-3 mt-3 mb-4">
                  <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Sortieren nach:</label>
                  <select
                    value={sortField}
                    onChange={(e) => setSortField(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Standard</option>
                    <option value="price">Preis pro Nacht</option>
                    <option value="distance">Entfernung zum Zentrum</option>
                    <option value="size">Größe</option>
                    <option value="guests">Gäste</option>
                    <option value="bedrooms">Schlafzimmer</option>
                    <option value="reviews">Bewertungen</option>
                    <option value="sponsored">Gesponsert</option>
                  </select>
                  {sortField && (
                    <button
                      onClick={() => setSortDirection(d => d === 'asc' ? 'desc' : 'asc')}
                      className="text-sm px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
                      title={sortDirection === 'asc' ? 'Aufsteigend' : 'Absteigend'}
                    >
                      {sortDirection === 'asc' ? '↑ Aufsteigend' : '↓ Absteigend'}
                    </button>
                  )}
                  {(filtersActive || sortField) && (
                    <button
                      onClick={resetFilters}
                      className="ml-auto text-sm text-red-600 hover:text-red-800"
                    >
                      Alles zurücksetzen
                    </button>
                  )}
                </div>
                {/* Filter grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Preis / Nacht</label>
                    <div className="flex gap-1">
                      <input type="number" min="0" placeholder="Min" value={filters.priceMin} onChange={(e) => updateFilter('priceMin', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                      <input type="number" min="0" placeholder="Max" value={filters.priceMax} onChange={(e) => updateFilter('priceMax', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Max. Entfernung (km)</label>
                    <input type="number" min="0" step="any" placeholder="z. B. 5" value={filters.distanceMax} onChange={(e) => updateFilter('distanceMax', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Größe (m²)</label>
                    <div className="flex gap-1">
                      <input type="number" min="0" placeholder="Min" value={filters.sizeMin} onChange={(e) => updateFilter('sizeMin', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                      <input type="number" min="0" placeholder="Max" value={filters.sizeMax} onChange={(e) => updateFilter('sizeMax', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Min. Gäste</label>
                    <input type="number" min="0" placeholder="z. B. 2" value={filters.guestsMin} onChange={(e) => updateFilter('guestsMin', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Min. Schlafzimmer</label>
                    <input type="number" min="0" placeholder="z. B. 1" value={filters.bedroomsMin} onChange={(e) => updateFilter('bedroomsMin', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Min. Bewertungen</label>
                    <input type="number" min="0" placeholder="z. B. 10" value={filters.reviewsMin} onChange={(e) => updateFilter('reviewsMin', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer py-1">
                      <input type="checkbox" checked={filters.hideSponsored} onChange={(e) => updateFilter('hideSponsored', e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                      Gesponserte ausblenden
                    </label>
                  </div>
                  <div className="col-span-2 md:col-span-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Detail-Status</label>
                    <div className="flex flex-wrap gap-3">
                      <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                        <input type="checkbox" checked={filters.detailHasDates} onChange={(e) => updateFilter('detailHasDates', e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                        <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>
                        Buchungstage vorhanden
                      </label>
                      <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                        <input type="checkbox" checked={filters.detailNoDates} onChange={(e) => updateFilter('detailNoDates', e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                        <span className="inline-block w-2 h-2 rounded-full bg-orange-400"></span>
                        Keine Buchungstage
                      </label>
                      <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                        <input type="checkbox" checked={filters.detailFailed} onChange={(e) => updateFilter('detailFailed', e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                        <span className="inline-block w-2 h-2 rounded-full bg-red-500"></span>
                        Fehlgeschlagen
                      </label>
                      <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                        <input type="checkbox" checked={filters.detailNotScraped} onChange={(e) => updateFilter('detailNotScraped', e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                        <span className="inline-block w-2 h-2 rounded-full bg-gray-400"></span>
                        Nicht aktualisiert
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {search.search_status === 'in_progress' || search.search_status === 'pending' ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
            <h2 className="text-xl font-semibold mt-6 mb-2">Inserate werden gesucht…</h2>
            <p className="text-gray-600">
              Das kann einen Moment dauern. HomeToGo wird nach Ferienwohnungen in {search.location} durchsucht.
            </p>
          </div>
        ) : search.search_status === 'failed' ? (
          <div className="bg-white rounded-lg shadow p-8">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-red-600 mb-2">Suche fehlgeschlagen</h2>
              <p className="text-gray-600">{search.error_message || 'Bei der Suche ist ein Fehler aufgetreten.'}</p>
            </div>
          </div>
        ) : listings && listings.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Map */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-semibold mb-4">Kartenansicht</h2>
              <div className="h-[600px]">
                <Map
                  listings={filteredListings}
                  center={mapCenter}
                  zoom={mapZoom}
                  hoveredListingId={hoveredListingId}
                  onHoverListing={handleMapHover}
                  onMapMove={handleMapMove}
                />
              </div>
            </div>

            {/* Listings List */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold">
                  Inserate
                  {filtersActive && (
                    <span className="text-sm font-normal text-gray-500"> ({filteredListings.length} von {listings?.length})</span>
                  )}
                </h2>
              </div>
              <div
                ref={listContainerRef}
                className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto"
              >
                {filteredListings.length === 0 && filtersActive && (
                  <div className="p-8 text-center text-gray-500">
                    <p>Kein Inserat passt zu den gewählten Filtern.</p>
                    <button onClick={resetFilters} className="mt-2 text-sm text-blue-600 hover:text-blue-800">Filter zurücksetzen</button>
                  </div>
                )}
                {filteredListings.map((listing: Listing) => (
                  <div
                    key={listing.id}
                    ref={(el) => { listingRefs.current[listing.id] = el; }}
                    className={`p-4 cursor-pointer transition-colors duration-200 ${
                      listing.id === hoveredListingId
                        ? 'bg-blue-50 border-l-4 border-l-blue-400'
                        : 'hover:bg-gray-50 border-l-4 border-l-transparent'
                    }`}
                    onClick={() => navigate(`/inserat/${listing.id}?returnTo=${encodeURIComponent(`/search/${searchId}?${searchParams.toString()}`)}`)}
                    onMouseEnter={() => handleListHover(listing.id)}
                    onMouseLeave={() => handleListHover(null)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900">{listing.title}</h3>
                        {listing.resolved_title && listing.resolved_title !== listing.title && (
                          <p className="text-sm text-gray-500 italic mt-0.5 truncate">{listing.resolved_title}</p>
                        )}
                      </div>
                      <button
                        onClick={(e) => handleToggleMark(e, listing.id)}
                        className={`flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                          listing.marked_for_detail
                            ? 'bg-purple-600 border-purple-600 text-white'
                            : 'border-gray-300 hover:border-purple-400'
                        }`}
                        title={listing.marked_for_detail ? 'Markierung für Detail-Aktualisierung aufheben' : 'Für Detail-Aktualisierung markieren'}
                      >
                        {listing.marked_for_detail && (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {listing.listing_type && (
                        <span className="inline-block px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                          {listing.listing_type}
                        </span>
                      )}
                      {listing.resolved_platform && (
                        <span className="inline-block px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 rounded">
                          {listing.resolved_platform}
                        </span>
                      )}
                      {listing.detail_scrape_status && (
                        listing.detail_scrape_status === 'completed' ? (
                          listing.booked_dates ? (
                            <span className="inline-block w-2 h-2 rounded-full bg-green-500" title="Buchungstage vorhanden"></span>
                          ) : (
                            <span className="inline-block w-2 h-2 rounded-full bg-orange-400" title="Aktualisiert — keine Buchungstage gefunden"></span>
                          )
                        ) : listing.detail_scrape_status === 'failed' ? (
                          <span className="inline-block w-2 h-2 rounded-full bg-red-500" title={`Detail-Aktualisierung fehlgeschlagen: ${listing.detail_scrape_error || 'unbekannter Fehler'}`}></span>
                        ) : listing.detail_scrape_status === 'in_progress' ? (
                          <span className="inline-block w-2 h-2 rounded-full bg-yellow-500 animate-pulse" title="Detail-Aktualisierung läuft"></span>
                        ) : (
                          <span className="inline-block w-2 h-2 rounded-full bg-gray-400" title={`Detail-Aktualisierung: ${DETAIL_STATUS_LABELS[listing.detail_scrape_status] ?? listing.detail_scrape_status}`}></span>
                        )
                      )}
                    </div>
                    <div className="mt-2 text-sm text-gray-600 grid grid-cols-2 gap-x-4 gap-y-1">
                      {listing.size && <p>Größe: {listing.size}</p>}
                      {listing.guests_count && <p>Gäste: {listing.guests_count}</p>}
                      {listing.bedrooms != null && <p>Schlafzimmer: {listing.bedrooms}</p>}
                      {listing.neighborhood && <p>Lage: {listing.neighborhood}</p>}
                      {listing.distance_to_center && <p>Entfernung: {listing.distance_to_center}</p>}
                      {listing.reviews && <p>Bewertungen: {listing.reviews}</p>}
                      {listing.price_per_night && (
                        <p>Preis: {listing.price_per_night} {listing.currency}/Nacht</p>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-400 italic">
                      {listing.sponsored && listing.sponsored !== 'None' ? listing.sponsored : 'Nicht gesponsert'}
                    </p>
                    {listing.resolved_url && (
                      <a
                        href={listing.resolved_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-blue-600 hover:text-blue-800 mt-2 inline-block"
                      >
                        Inserat öffnen →
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Keine Inserate gefunden</h2>
            <p className="text-gray-600">
              Für {search.location} wurden keine Ferienwohnungen gefunden. Versuchen Sie einen anderen Ort.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default SearchResults;
